import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, Space, Typography, Tabs, Card, Tag, Divider, Spin, Empty, Popconfirm, message } from 'antd';
import dayjs from 'dayjs';
import { 
  EditOutlined, MailOutlined, PhoneOutlined, BankOutlined, DollarOutlined, 
  CalendarOutlined, EnvironmentOutlined, ArrowLeftOutlined, CreditCardOutlined,
  HeartOutlined, TrophyOutlined, HistoryOutlined, GiftOutlined,
  AppleOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { donorAPI } from '../services/api';
import './InviteDonorModal.css';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface EditDonorModalProps {
  visible: boolean;
  donor: any | null;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  beneficiaries: any[];
}

const EditDonorModal: React.FC<EditDonorModalProps> = ({
  visible,
  donor,
  onCancel,
  onSubmit,
  beneficiaries
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [donorDetails, setDonorDetails] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Membership (the donor's live monthly gift), read from Stripe rather than
  // from our own status column, because the two have disagreed before.
  const [membership, setMembership] = useState<any>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipBusy, setMembershipBusy] = useState(false);

  // Fetch detailed donor information when modal opens
  useEffect(() => {
    if (donor && visible && donor.id) {
      fetchDonorDetails(donor.id);
      fetchMembership(donor.id);
    } else {
      setDonorDetails(null);
      setMembership(null);
    }
  }, [donor, visible]);

  // Populate form when donor data is available
  useEffect(() => {
    if (donor && visible) {
      // Parse city and state from cityState field
      const cityStateParts = donor.cityState?.split(', ') || ['', ''];
      const city = cityStateParts[0] || '';
      const state = cityStateParts[1] || '';
      
              // Extract numeric values from donation strings (e.g., "$500" -> "500")
              // Handle both formatted strings like "$15" and plain numbers
              const donationAmount = donor.donation ? 
                (typeof donor.donation === 'string' ? donor.donation.replace(/[^0-9.]/g, '') : String(donor.donation)) : 
                '0';
              const oneTimeAmount = donor.oneTime ? 
                (typeof donor.oneTime === 'string' ? donor.oneTime.replace(/[^0-9.]/g, '') : String(donor.oneTime)) : 
                '0';
      
      // Parse last donated date as a dayjs object, not a native Date.
      //
      // antd 5's DatePicker is built on dayjs and calls dayjs methods on
      // whatever value the form holds. A native Date has none of them, so
      // opening this modal for any donor who had actually donated threw during
      // render and the whole panel fell back to the ErrorBoundary: "Something
      // went wrong". Donors with no donation date were fine, because the value
      // stayed null, which is why it looked like only certain rows were broken.
      //
      // handleSubmit already called values.lastDonated.format(...), a dayjs
      // method, so the write path assumed dayjs all along. Only the populate
      // path disagreed.
      let lastDonatedDate: dayjs.Dayjs | null = null;
      if (donor.lastDonated && donor.lastDonated !== 'Never') {
        const parsed = dayjs(donor.lastDonated);
        lastDonatedDate = parsed.isValid() ? parsed : null;
      }

      form.setFieldsValue({
        name: donor.name || '',
        email: donor.email || '',
        contact: donor.contact || '',
        city: city,
        state: state,
        zipCode: '',
        beneficiary: donor.originalData?.beneficiary_id?.toString() || '',
        coworking: donor.coworking || 'No',
        membership:
          donor.invite_type ||
          (donor.coworking === 'Yes' || donor.coworking === true
            ? 'coworking'
            : 'standard'),
        donation: donationAmount !== '0' ? donationAmount : '',
        oneTime: oneTimeAmount !== '0' ? oneTimeAmount : '',
        lastDonated: lastDonatedDate,
        notes: donor.notes || ''
      });
    } else if (!visible) {
      form.resetFields();
      setActiveTab('overview');
    }
  }, [donor, visible, form]);

  const fetchDonorDetails = async (donorId: number) => {
    setLoadingDetails(true);
    try {
      const response = await donorAPI.getDonorDetails(donorId);
      if (response.success && response.data) {
        setDonorDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching donor details:', error);
      // If endpoint doesn't exist, use mock/fallback data structure
      setDonorDetails({
        payment_methods: [],
        monthly_donation: null,
        current_beneficiary: null,
        donation_history: [],
        discount_redemptions: [],
        total_savings: 0
      });
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchMembership = async (donorId: number) => {
    setMembershipLoading(true);
    try {
      const response = await donorAPI.getDonorMembership(donorId);
      setMembership(response?.success ? response.data : null);
    } catch (error) {
      console.error('Error fetching donor membership:', error);
      // No membership state means no button, which is the safe direction: an
      // admin asking why is cheaper than a wrong action on someone's card.
      setMembership(null);
    } finally {
      setMembershipLoading(false);
    }
  };

  /**
   * Stop or restart the donor's monthly gift.
   *
   * Only ever acts on the subscription they already have. Stopping schedules
   * it for the end of the period they paid for; restarting clears that. There
   * is no path here that creates a subscription or takes a payment.
   */
  const handleMembershipAction = async (action: 'cancel' | 'resume') => {
    if (!donor?.id) return;
    setMembershipBusy(true);
    try {
      const response = await donorAPI.setDonorMembership(donor.id, action);
      message.success(
        response?.message ||
          (action === 'cancel'
            ? 'That gift will stop at the end of the period they have paid for.'
            : 'That gift will keep going.'),
      );
    } catch (error: any) {
      message.error(
        error?.message || 'That did not go through. Give it another try?',
      );
    } finally {
      // Re-read either way. On success it shows the new state; on failure it
      // shows what Stripe actually says, which is usually why it refused.
      await fetchMembership(donor.id);
      setMembershipBusy(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Format the date
      if (values.lastDonated) {
        values.lastDonated = values.lastDonated.format('YYYY-MM-DD');
      }
      
      // Combine city, state, and ZIP for display/storage
      if (values.city && values.state) {
        values.cityState = `${values.city}, ${values.state}${values.zipCode ? ' ' + values.zipCode : ''}`;
      }
      
      onSubmit(values);
    } catch (error) {
      console.error('Validation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setActiveTab('overview');
    onCancel();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Modal
      title={
        <div className="modal-header">
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={handleCancel}
            className="back-btn"
          />
          <div className="header-content">
            <Title level={3} className="modal-title">
              {donor?.name || 'Donor'} Profile
            </Title>
            <Text className="modal-subtitle">View and edit donor information</Text>
          </div>
        </div>
      }
      open={visible && !!donor}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      width={1000}
      className="invite-donor-modal donor-profile-modal"
      destroyOnClose={false}
    >
      <Spin spinning={loadingDetails}>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          size="large"
          className="donor-profile-tabs"
        >
          {/* Overview Tab */}
          <TabPane tab={<span><InfoCircleOutlined /> Overview</span>} key="overview">
            <Form
              form={form}
              layout="vertical"
              requiredMark="optional"
              className="invite-donor-form"
              initialValues={{
                coworking: donor?.coworking || 'No',
                membership:
                  donor?.invite_type ||
                  (donor?.coworking === 'Yes' || donor?.coworking === true
                    ? 'coworking'
                    : 'standard'),
              }}
            >
              <div className="form-sections">
                {/* Personal Information Section */}
                <div className="form-section">
                  <Title level={4} className="section-title">
                    Personal Information
                  </Title>
                  <div className="form-row">
                    <Form.Item
                      name="name"
                      label="Donor Name"
                      rules={[{ required: true, message: 'Please enter the donor name' }]}
                      className="form-item"
                    >
                      <Input 
                        placeholder="Enter donor name"
                        prefix={<EditOutlined />}
                        size="large"
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="email"
                      label="Email Address"
                      rules={[
                        { required: true, message: 'Please enter the email address' },
                        { type: 'email', message: 'Please enter a valid email address' }
                      ]}
                      className="form-item"
                    >
                      <Input 
                        placeholder="Enter email address"
                        prefix={<MailOutlined />}
                        size="large"
                      />
                    </Form.Item>
                  </div>
                  
                  <div className="form-row">
                    <Form.Item
                      name="contact"
                      label="Contact Number"
                      rules={[{ required: true, message: 'Please enter the contact number' }]}
                      className="form-item"
                    >
                      <Input 
                        placeholder="Enter contact number"
                        prefix={<PhoneOutlined />}
                        size="large"
                      />
                    </Form.Item>
                  </div>
                  
                  <div className="form-row form-row-three">
                    <Form.Item
                      name="city"
                      label="City"
                      rules={[{ required: true, message: 'Please enter city' }]}
                      className="form-item"
                    >
                      <Input 
                        placeholder="Enter city"
                        prefix={<EnvironmentOutlined />}
                        size="large"
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="state"
                      label="State"
                      rules={[{ required: true, message: 'Please enter state' }]}
                      className="form-item"
                    >
                      <Input 
                        placeholder="Enter state"
                        size="large"
                      />
                    </Form.Item>
                    
                    <Form.Item
                      name="zipCode"
                      label="ZIP Code"
                      rules={[{ required: false }]}
                      className="form-item"
                    >
                      <Input 
                        placeholder="Enter ZIP"
                        size="large"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Beneficiary & Donation Section */}
                <div className="form-section">
                  <Title level={4} className="section-title">
                    Charity & Donation Information
                  </Title>
                  <div className="form-row">
                    <Form.Item
                      name="beneficiary"
                      label="Selected Charity Name"
                      rules={[{ required: false }]}
                      className="form-item"
                    >
                      <Select
                        placeholder="Select charity"
                        size="large"
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                        notFoundContent="No charities found"
                      >
                        {beneficiaries.map((beneficiary: any) => (
                          <Option key={beneficiary.id} value={beneficiary.id?.toString()}>
                            {beneficiary.name || 'Unknown'}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    
                    <Form.Item
                      name="membership"
                      label="Membership"
                      rules={[{ required: true, message: 'Please select a membership type' }]}
                      className="form-item"
                    >
                      <Select size="large">
                        <Option value="standard">Standard donor</Option>
                        <Option value="coworking">Coworking member</Option>
                        <Option value="team">THRIVE team</Option>
                      </Select>
                    </Form.Item>
                  </div>
                  
                  <div className="form-row">
                    <Form.Item
                      name="donation"
                      label="Donation Amount"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value === undefined || value === null || value === '') {
                              return Promise.resolve();
                            }
                            const numeric = parseFloat(String(value));
                            return numeric > 0
                              ? Promise.resolve()
                              : Promise.reject(new Error('Donation amount must be greater than 0'));
                          }
                        }
                      ]}
                      className="form-item"
                    >
                      <Input 
                        placeholder="Enter donation amount"
                        prefix={<DollarOutlined />}
                        size="large"
                        type="number"
                        min={0}
                      />
                    </Form.Item>
                    
              <Form.Item
                name="oneTime"
                label="One Time Gifts"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                      }
                      const numeric = parseFloat(String(value));
                      return numeric > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error('One time gift must be greater than 0'));
                    }
                  }
                ]}
                className="form-item"
              >
                <Input 
                  placeholder="Enter one time gift amount (optional)"
                  prefix={<DollarOutlined />}
                  size="large"
                  type="number"
                  min={0}
                />
              </Form.Item>
                  </div>
                  
                  <div className="form-row">
                    <Form.Item
                      name="lastDonated"
                      label="Last Donated Date"
                      className="form-item full-width"
                    >
                      <DatePicker
                        placeholder="Select last donated date"
                        size="large"
                        style={{ width: '100%' }}
                        format="MMMM DD, YYYY"
                        disabledDate={(current) => current && current > dayjs().endOf('day')}
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Monthly Gift Section
                    The one place in the panel that can stop or restart a live
                    subscription. Both directions are reversible by design: the
                    button never deletes a subscription and never starts one. */}
                <div className="form-section">
                  <Title level={4} className="section-title">
                    Monthly Gift
                  </Title>
                  {membershipLoading ? (
                    <Spin size="small" />
                  ) : membership?.action ? (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Space wrap size="middle">
                        <Tag color={membership.action === 'resume' ? 'orange' : 'green'}>
                          {membership.action === 'resume' ? 'Stopping soon' : 'Active'}
                        </Tag>
                        {membership.target?.amount != null && (
                          <Text strong>
                            {formatCurrency(Number(membership.target.amount))} a month
                          </Text>
                        )}
                        {membership.target?.currentPeriodEnd && (
                          <Text type="secondary">
                            {membership.action === 'resume'
                              ? `Ends ${formatDate(membership.target.currentPeriodEnd)}`
                              : `Renews ${formatDate(membership.target.currentPeriodEnd)}`}
                          </Text>
                        )}
                      </Space>

                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {membership.action === 'cancel'
                          ? 'Stopping it keeps the month they have already paid for, and you can restart it from here any time before that runs out.'
                          : 'This gift is set to stop at the end of the period they paid for. Restarting it keeps it going, with nothing charged today.'}
                      </Text>

                      <Popconfirm
                        title={
                          membership.action === 'cancel'
                            ? 'Stop this monthly gift?'
                            : 'Restart this monthly gift?'
                        }
                        description={
                          membership.action === 'cancel'
                            ? 'They keep what they have paid for, and you can undo this until the period ends.'
                            : 'Billing carries on as normal. Nothing is charged today.'
                        }
                        okText={
                          membership.action === 'cancel'
                            ? 'Yes, stop it'
                            : 'Yes, restart it'
                        }
                        cancelText="Leave it as it is"
                        okButtonProps={{ danger: membership.action === 'cancel' }}
                        onConfirm={() => handleMembershipAction(membership.action)}
                      >
                        <Button
                          size="large"
                          danger={membership.action === 'cancel'}
                          type={membership.action === 'resume' ? 'primary' : 'default'}
                          loading={membershipBusy}
                        >
                          {membership.action === 'cancel'
                            ? 'Stop monthly gift'
                            : 'Restart monthly gift'}
                        </Button>
                      </Popconfirm>
                    </Space>
                  ) : (
                    <Text type="secondary">
                      {membership?.reason || 'No monthly gift on file for this donor.'}
                    </Text>
                  )}
                </div>

                {/* Additional Notes Section */}
                <div className="form-section">
                  <Title level={4} className="section-title">
                    Additional Information
                  </Title>
                  <Form.Item
                    name="notes"
                    label="Notes"
                    className="form-item full-width"
                  >
                    <TextArea 
                      placeholder="Enter any additional notes about the donor"
                      rows={3}
                      size="large"
                    />
                  </Form.Item>
                </div>
              </div>

              {/* Form Actions */}
              <div className="form-actions">
                <Space size="middle">
                  <Button 
                    size="large" 
                    onClick={handleCancel}
                    className="cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="primary" 
                    size="large" 
                    onClick={handleSubmit}
                    loading={loading}
                    icon={<EditOutlined />}
                    className="submit-btn"
                  >
                    Update Donor
                  </Button>
                </Space>
              </div>
            </Form>
          </TabPane>

          {/* Payment Methods Tab */}
          <TabPane tab={<span><CreditCardOutlined /> Payment Methods</span>} key="payment">
            <div style={{ padding: '20px 0' }}>
              {donorDetails?.payment_methods && donorDetails.payment_methods.length > 0 ? (
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {donorDetails.payment_methods.map((method: any, index: number) => (
                    <Card key={index} size="small">
                      <Space>
                        {method.type === 'apple_pay' ? (
                          <AppleOutlined style={{ fontSize: '24px', color: '#000' }} />
                        ) : (
                          <CreditCardOutlined style={{ fontSize: '24px', color: '#DB8633' }} />
                        )}
                        <div>
                          <Text strong>
                            {method.type === 'apple_pay' ? 'Apple Pay' : 
                             method.brand ? `${method.brand.toUpperCase()} •••• ${method.last4}` : 
                             'Credit Card'}
                          </Text>
                          {method.is_default && (
                            <Tag color="green" style={{ marginLeft: '8px' }}>Default</Tag>
                          )}
                          <br />
                          <Text type="secondary">Expires {method.exp_month}/{method.exp_year}</Text>
                        </div>
                      </Space>
                    </Card>
                  ))}
                </Space>
              ) : (
                <Empty 
                  description="No payment methods on file"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </TabPane>

          {/* Monthly Donation Tab */}
          <TabPane tab={<span><DollarOutlined /> Monthly Donation</span>} key="monthly">
            <div style={{ padding: '20px 0' }}>
              <Card>
                {donorDetails?.monthly_donation ? (
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">Monthly Donation Amount</Text>
                      <Title level={2} style={{ margin: '8px 0', color: '#DB8633' }}>
                        {formatCurrency(donorDetails.monthly_donation.amount)}
                      </Title>
                    </div>
                    <Divider />
                    <div>
                      <Text strong>Status: </Text>
                      <Tag color={donorDetails.monthly_donation.active ? 'green' : 'red'}>
                        {donorDetails.monthly_donation.active ? 'Active' : 'Inactive'}
                      </Tag>
                    </div>
                    {donorDetails.monthly_donation.start_date && (
                      <div>
                        <Text strong>Started: </Text>
                        <Text>{formatDate(donorDetails.monthly_donation.start_date)}</Text>
                      </div>
                    )}
                    {donorDetails.monthly_donation.next_charge_date && (
                      <div>
                        <Text strong>Next Charge: </Text>
                        <Text>{formatDate(donorDetails.monthly_donation.next_charge_date)}</Text>
                      </div>
                    )}
                  </Space>
                ) : (
                  <Empty 
                    description="No monthly donation set up"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Card>
            </div>
          </TabPane>

          {/* Current Beneficiary Tab */}
          <TabPane tab={<span><HeartOutlined /> Current Charity</span>} key="beneficiary">
            <div style={{ padding: '20px 0' }}>
              {donorDetails?.current_beneficiary ? (
                <Card>
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                      <Text strong style={{ fontSize: '16px' }}>
                        {donorDetails.current_beneficiary.name}
                      </Text>
                      <br />
                      <Text type="secondary">
                        {donorDetails.current_beneficiary.category || 'Charity'}
                      </Text>
                    </div>
                    <Divider />
                    <div>
                      <Text type="secondary">Donation Amount</Text>
                      <Title level={3} style={{ margin: '8px 0', color: '#DB8633' }}>
                        {formatCurrency(donorDetails.current_beneficiary.amount)}
                      </Title>
                    </div>
                    {donorDetails.current_beneficiary.start_date && (
                      <div>
                        <Text strong>Since: </Text>
                        <Text>{formatDate(donorDetails.current_beneficiary.start_date)}</Text>
                      </div>
                    )}
                  </Space>
                </Card>
              ) : (
                <Empty 
                  description="No current charity"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </TabPane>

          {/* Donation History Tab */}
          <TabPane tab={<span><HistoryOutlined /> Donation History</span>} key="history">
            <div style={{ padding: '20px 0' }}>
              {donorDetails?.donation_history && donorDetails.donation_history.length > 0 ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {donorDetails.donation_history.map((donation: any, index: number) => (
                    <Card key={index} size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>{donation.beneficiary_name || 'Charity'}</Text>
                          <br />
                          <Text type="secondary">{formatDate(donation.date)}</Text>
                        </div>
                        <Text strong style={{ fontSize: '18px', color: '#DB8633' }}>
                          {formatCurrency(donation.amount)}
                        </Text>
                      </div>
                      {donation.type && (
                        <Tag style={{ marginTop: '8px' }}>
                          {donation.type === 'monthly' ? 'Monthly' : 'One-time'}
                        </Tag>
                      )}
                    </Card>
                  ))}
                </Space>
              ) : (
                <Empty 
                  description="No donation history available"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </TabPane>

          {/* Discount Redemptions Tab */}
          <TabPane tab={<span><GiftOutlined /> Discount Redemptions</span>} key="discounts">
            <div style={{ padding: '20px 0' }}>
              {donorDetails?.discount_redemptions && donorDetails.discount_redemptions.length > 0 ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {donorDetails.discount_redemptions.map((redemption: any, index: number) => (
                    <Card key={index} size="small">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <Text strong>{redemption.vendor_name || 'Vendor'}</Text>
                          <br />
                          <Text type="secondary">{redemption.discount_name || 'Discount'}</Text>
                          <br />
                          <Text type="secondary">{formatDate(redemption.date)}</Text>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Tag color="green">Saved {formatCurrency(redemption.savings)}</Tag>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>
                            {redemption.location || ''}
                          </Text>
                        </div>
                      </div>
                    </Card>
                  ))}
                  <Divider />
                  <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text strong style={{ fontSize: '16px' }}>Total Savings</Text>
                      <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                        {formatCurrency(donorDetails.total_savings || 0)}
                      </Title>
                    </div>
                  </Card>
                </Space>
              ) : (
                <Empty 
                  description="No discount redemptions"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </div>
          </TabPane>

        </Tabs>
      </Spin>
    </Modal>
  );
};

export default EditDonorModal;
